/**
 * Track Aspect Rose
 *
 * SVG rose diagram showing the proportion of track points on each compass aspect.
 * Uses octagon segments (like the avalanche aspect rose) where each segment is
 * centered on a compass direction. The filled radius of each segment is proportional
 * to the fraction of points on that bearing.
 */

import { LitElement, html, svg, css } from 'lit'
import { customElement, property } from 'lit/decorators.js'

/** Counts of track points per compass direction, indexed 0=N, 1=NE, ..., 7=NW */
export type AspectCounts = [number, number, number, number, number, number, number, number]

const DIRECTIONS = ['N', 'NE', 'E', 'SE', 'S', 'SW', 'W', 'NW'] as const

@customElement('track-aspect-rose')
export class TrackAspectRose extends LitElement {
  static override styles = css`
    :host {
      display: flex;
      align-items: center;
      justify-content: center;
      width: 100%;
      height: 100%;
    }

    svg {
      max-width: 100%;
      max-height: 100%;
    }
  `

  @property({ type: Array })
  counts: AspectCounts = [0, 0, 0, 0, 0, 0, 0, 0]

  override render(): unknown {
    const size = 160
    const cx = size / 2
    const cy = size / 2
    const maxRadius = 60
    const labelRadius = 72

    const total = this.counts.reduce((s, c) => s + c, 0)
    if (total === 0) {
      return html`<div style="opacity:0.5;font-size:13px">No aspect data yet</div>`
    }

    // Edge angles — between adjacent directions, so each segment is centered on its direction
    // Edge i sits at direction_angle + 22.5° (halfway to next direction)
    const edgeAngles = DIRECTIONS.map((_, i) => ((i * 45 - 90 + 22.5) * Math.PI) / 180)

    // Outline: full octagon segments at maxRadius (unfilled background)
    const outlineSegments = DIRECTIONS.map((_, i) => {
      const eL = edgeAngles[(i + 7) % 8]
      const eR = edgeAngles[i]
      return svg`<path
        d="${this.wedgePath(cx, cy, maxRadius, eL, eR)}"
        fill="none"
        stroke="rgba(255,255,255,0.3)"
        stroke-width="0.75"
      />`
    })

    const maxCount = Math.max(...this.counts)

    // Filled segments — radius proportional to fraction of max (standard aspect rose convention)
    const filledSegments = DIRECTIONS.map((_, i) => {
      const count = this.counts[i]
      const fraction = maxCount > 0 ? count / maxCount : 0
      const r = fraction * maxRadius

      if (r < 1) return svg``

      const eL = edgeAngles[(i + 7) % 8]
      const eR = edgeAngles[i]
      const pct = total > 0 ? Math.round((count / total) * 100) : 0

      return svg`<path
        d="${this.wedgePath(cx, cy, r, eL, eR)}"
        fill="rgba(70,130,200,0.85)"
        stroke="rgba(50,100,180,1)"
        stroke-width="1"
      >
        <title>${DIRECTIONS[i]}: ${pct}% (${count} pts)</title>
      </path>`
    })

    // Reference rings at 25%, 50%, 75%, 100%
    const rings = [0.25, 0.5, 0.75, 1.0].map(frac => {
      const r = frac * maxRadius
      // Draw octagon ring at this radius
      const pts = edgeAngles.map(a => `${cx + r * Math.cos(a)},${cy + r * Math.sin(a)}`).join(' ')
      return svg`<polygon points="${pts}"
        fill="none" stroke="rgba(255,255,255,0.25)" stroke-width="0.5" />`
    })

    // Spoke lines from center to each edge vertex at full radius
    const spokes = edgeAngles.map(a => {
      const x = cx + maxRadius * Math.cos(a)
      const y = cy + maxRadius * Math.sin(a)
      return svg`<line x1="${cx}" y1="${cy}" x2="${x}" y2="${y}"
        stroke="rgba(255,255,255,0.25)" stroke-width="0.5" />`
    })

    // Direction labels — centered on each segment
    const labels = DIRECTIONS.map((dir, i) => {
      const angle = ((i * 45 - 90) * Math.PI) / 180
      const lx = cx + labelRadius * Math.cos(angle)
      const ly = cy + labelRadius * Math.sin(angle)
      return svg`<text x="${lx}" y="${ly}" text-anchor="middle" dominant-baseline="central"
        font-size="11" fill="white" font-weight="700">${dir}</text>`
    })

    return html`<svg width="${size}" height="${size}" viewBox="0 0 ${size} ${size}">
      ${rings} ${spokes} ${outlineSegments} ${filledSegments} ${labels}
    </svg>`
  }

  /** Triangle wedge from center to two octagon edge vertices at radius r */
  private wedgePath(cx: number, cy: number, r: number, aL: number, aR: number): string {
    const x1 = cx + r * Math.cos(aL)
    const y1 = cy + r * Math.sin(aL)
    const x2 = cx + r * Math.cos(aR)
    const y2 = cy + r * Math.sin(aR)
    return `M${cx},${cy} L${x1},${y1} L${x2},${y2} Z`
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'track-aspect-rose': TrackAspectRose
  }
}
